import React, { useContext, useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  FormHelperText,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, provider, firebaseInitialized, db } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { CalculatorContext } from '../context/CalculatorContext';

const InputForm = () => {
  const { inputs, updateInput, computeResults } = useContext(CalculatorContext);
  // Local state for errors
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInAttempted, setSignInAttempted] = useState(false);
  const [user, loading, authStateError] = useAuthState(auth);

  useEffect(() => {
    if (authStateError) {
      console.error(authStateError);
      setAuthError(authStateError.message);
    }
  }, [authStateError]);

  const attemptSignIn = async () => {
    setAuthError('');
    setSignInLoading(true);
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        setAuthError('Sign-in popup was blocked or closed. Please allow pop-ups and retry.');
      } else {
        setAuthError(err.message);
      }
    } finally {
      setSignInLoading(false);
    }
  };

  useEffect(() => {
    if (firebaseInitialized && !loading && !user && !signInAttempted) {
      setSignInAttempted(true);
      attemptSignIn();
    }
  }, [firebaseInitialized, loading, user, signInAttempted]);

  useEffect(() => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      setDoc(
        userRef,
        {
          uid: user.uid,
          displayName: user.displayName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
        },
        { merge: true }
      ).catch((err) => console.error('Error saving user info', err));
    }
  }, [user]);
  if (!firebaseInitialized) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        Firebase configuration not provided. Unable to initialize.
      </Typography>
    );
  }

  if (!user) {
    return (
      <>
        {authError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {authError}
          </Alert>
        )}
        {loading || signInLoading ? (
          <>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Signing in...
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </>
        ) : (
          <Button variant="contained" onClick={() => attemptSignIn()} sx={{ mt: 2 }}>
            Retry Sign-In
          </Button>
        )}
      </>
    );
  }

  const validate = () => {
    let tempErrors = {};

    if (inputs.roofArea <= 0) {
      tempErrors.roofArea = 'Roof area must be greater than 0';
    }
    if (inputs.existingAlbedo < 0 || inputs.existingAlbedo > 1) {
      tempErrors.existingAlbedo = 'Albedo must be between 0 and 1';
    }
    if (inputs.coolAlbedo < 0 || inputs.coolAlbedo > 1) {
      tempErrors.coolAlbedo = 'Albedo must be between 0 and 1';
    }
    if (inputs.cop <= 0) {
      tempErrors.cop = 'COP must be greater than 0';
    }
    if (inputs.costPerKWh <= 0) {
      tempErrors.costPerKWh = 'Cost per kWh must be greater than 0';
    }
    if (inputs.materialCostPerM2 < 0) {
      tempErrors.materialCostPerM2 = 'Material cost cannot be negative';
    }
    if (inputs.laborCostPerM2 < 0) {
      tempErrors.laborCostPerM2 = 'Labor cost cannot be negative';
    }
    if (inputs.emissionFactor < 0) {
      tempErrors.emissionFactor = 'Emission factor cannot be negative';
    }
    if (inputs.serviceLife <= 0) {
      tempErrors.serviceLife = 'Service life must be greater than 0';
    }
    if (inputs.annualDegradation < 0 || inputs.annualDegradation > 100) {
      tempErrors.annualDegradation = 'Degradation must be 0–100%';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let parsedValue;
    if (type === 'checkbox') {
      parsedValue = checked;
    } else if (value === '') {
      parsedValue = '';
    } else {
      parsedValue = parseFloat(value);
      if (Number.isNaN(parsedValue)) {
        parsedValue = value;
      }
    }

    updateInput(name, parsedValue);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      computeResults(user);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        {/* Location & Climate */}
        <Grid item xs={12} md={6}>
          <TextField
            label="City"
            name="city"
            value={inputs.city}
            onChange={(e) => updateInput('city', e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Avg Solar Irradiance (W/m²)"
            name="averageSolarIrradiance"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.averageSolarIrradiance}
            onChange={handleChange}
            fullWidth
          />
          <FormHelperText>Typical range: 0–1200 W/m²</FormHelperText>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Cooling Days/Year"
            name="coolingDaysPerYear"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.coolingDaysPerYear}
            onChange={handleChange}
            fullWidth
          />
          <FormHelperText>Number of days AC runs</FormHelperText>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Hours/Day"
            name="hoursPerDay"
            type="number"
            inputProps={{ step: 1, min: 0, max: 24 }}
            value={inputs.hoursPerDay}
            onChange={handleChange}
            fullWidth
          />
          <FormHelperText>Typical range: 0–24 hours</FormHelperText>
        </Grid>

        {/* Building Specs */}
        <Grid item xs={6} md={3}>
          <TextField
            label="Roof Area (m²)"
            name="roofArea"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.roofArea}
            onChange={handleChange}
            fullWidth
            error={!!errors.roofArea}
            helperText={errors.roofArea}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Existing Albedo"
            name="existingAlbedo"
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            value={inputs.existingAlbedo}
            onChange={handleChange}
            fullWidth
            error={!!errors.existingAlbedo}
            helperText={errors.existingAlbedo}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Roof Type</InputLabel>
            <Select
              name="roofType"
              value={inputs.roofType}
              label="Roof Type"
              onChange={(e) => updateInput('roofType', e.target.value)}
            >
              <MenuItem value="Concrete">Concrete</MenuItem>
              <MenuItem value="Metal">Metal</MenuItem>
              <MenuItem value="GI Sheet">GI Sheet</MenuItem>
              <MenuItem value="Asphalt">Asphalt Shingles</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={inputs.insulated}
                name="insulated"
                onChange={(e) => updateInput('insulated', e.target.checked)}
                color="primary"
              />
            }
            label="Insulated Roof"
          />
        </Grid>
        {inputs.insulated && (
          <Grid item xs={6} md={3}>
            <TextField
              label="R-Value"
              name="rValue"
              type="number"
              inputProps={{ step: 0.1, min: 0 }}
              value={inputs.rValue}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Roof Slope</InputLabel>
            <Select
              name="roofSlope"
              value={inputs.roofSlope}
              label="Roof Slope"
              onChange={(e) => updateInput('roofSlope', e.target.value)}
            >
              <MenuItem value="Flat">Flat (0–10°)</MenuItem>
              <MenuItem value="Low Slope">Low Slope (10–30°)</MenuItem>
              <MenuItem value="Steep">Steep (&gt;30°)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Roof Age (yrs)"
            name="roofAge"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.roofAge}
            onChange={handleChange}
            fullWidth
          />
        </Grid>

        {/* Energy Specs */}
        <Grid item xs={6} md={3}>
          <TextField
            label="Cooling Capacity (TR)"
            name="coolingCapacity"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.coolingCapacity}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="COP"
            name="cop"
            type="number"
            inputProps={{ step: 0.1, min: 0 }}
            value={inputs.cop}
            onChange={handleChange}
            fullWidth
            error={!!errors.cop}
            helperText={errors.cop}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Cost per kWh (₹)"
            name="costPerKWh"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            value={inputs.costPerKWh}
            onChange={handleChange}
            fullWidth
            error={!!errors.costPerKWh}
            helperText={errors.costPerKWh}
          />
        </Grid>

        {/* Cool Roof Specs */}
        <Grid item xs={6} md={3}>
          <TextField
            label="Cool Roof Albedo"
            name="coolAlbedo"
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            value={inputs.coolAlbedo}
            onChange={handleChange}
            fullWidth
            error={!!errors.coolAlbedo}
            helperText={errors.coolAlbedo}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Thermal Emittance"
            name="thermalEmittance"
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            value={inputs.thermalEmittance}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="SRI"
            name="sri"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.sri}
            onChange={(e) => updateInput('sri', parseInt(e.target.value, 10))}
            fullWidth
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Material Cost (₹/m²)"
            name="materialCostPerM2"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.materialCostPerM2}
            onChange={handleChange}
            fullWidth
            error={!!errors.materialCostPerM2}
            helperText={errors.materialCostPerM2}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Labor Cost (₹/m²)"
            name="laborCostPerM2"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.laborCostPerM2}
            onChange={handleChange}
            fullWidth
            error={!!errors.laborCostPerM2}
            helperText={errors.laborCostPerM2}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Emission Factor (kg CO₂/kWh)"
            name="emissionFactor"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            value={inputs.emissionFactor}
            onChange={handleChange}
            fullWidth
            error={!!errors.emissionFactor}
            helperText={errors.emissionFactor}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Service Life (yrs)"
            name="serviceLife"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={inputs.serviceLife}
            onChange={handleChange}
            fullWidth
            error={!!errors.serviceLife}
            helperText={errors.serviceLife}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Annual Degradation (%)"
            name="annualDegradation"
            type="number"
            inputProps={{ step: 0.1, min: 0, max: 100 }}
            value={inputs.annualDegradation}
            onChange={handleChange}
            fullWidth
            error={!!errors.annualDegradation}
            helperText={errors.annualDegradation}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12} md={6}>
          <Button variant="contained" color="primary" type="submit" fullWidth>
            Calculate Savings
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default InputForm;
